import React, { useState, useContext, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Route, useParams, Link, useLocation } from 'react-router-dom';
import { AppContext } from '@/AppContext';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Drawer, Typography, Divider, MenuList, MenuItem, Button } from '@mui/material';
import Topbar from './components/Topbar/Topbar';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import PersonOutlineIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import FolderOutlinedIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import GearOutlinedIcon from '@mui/icons-material/Settings';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TextsmsIcon from '@mui/icons-material/Textsms';
import ChatIcon from '@mui/icons-material/Chat';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShopIcon from '@mui/icons-material/Shop';
import BallotIcon from '@mui/icons-material/Ballot';

const PREFIX = 'Main';

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  main: `${PREFIX}-main`,
  main__container: `${PREFIX}-main__container`,
  drawer: `${PREFIX}-drawer`,
  drawer_reduced: `${PREFIX}-drawer_reduced`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  drawer__header: `${PREFIX}-drawer__header`,
  menuItem: `${PREFIX}-menuItem`,
  menuItem__icon: `${PREFIX}-menuItem__icon`,
  backToMeetups: `${PREFIX}-backToMeetups`,
  backToMeetups__label: `${PREFIX}-backToMeetups__label`,
  backToMeetups__arrow: `${PREFIX}-backToMeetups__arrow`,
  backToMeetups__arrow_rotated: `${PREFIX}-backToMeetups__arrow_rotated`,
  divider: `${PREFIX}-divider`,
  spacer: `${PREFIX}-spacer`,
  menuReducer: `${PREFIX}-menuReducer`,
  hider: `${PREFIX}-hider`,
  centerer: `${PREFIX}-centerer`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    maxHeight: 'calc(100% - 64px)',
    flexGrow: 1,
    maxWidth: '100%',
  },

  [`& .${classes.main}`]: {
    flexGrow: 1,
    overflowY: 'auto',
    overflowX: 'auto',
  },

  [`& .${classes.main__container}`]: {
    maxWidth: 1200,
    margin: '0 auto',
  },

  [`& .${classes.main__container}--wide`]: {
    maxWidth: 1600,
  },

  [`& .${classes.drawer}`]: {
    width: 240,
    flexShrink: 0,
    padding: '0',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s',
  },

  [`& .${classes.drawer_reduced}`]: {
    width: 66,
  },

  [`& .${classes.drawerPaper}`]: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflowX: 'hidden',
  },

  [`& .${classes.drawer__header}`]: {
    color: '#90A4AE',
    fontSize: 11,
    padding: '21px 16px 11px 16px',
  },

  [`& .${classes.menuItem}`]: {
    color: '#37474F',
    fontSize: 14,
    padding: '13px 16px',
  },

  [`& .MuiMenuItem-root.Mui-selected`]: {
    color: '#1976d2',
    fontWeight: 500,
    '& .MuiTypography-root': {
      fontWeight: 500,
      color: '#1976d2',
    },
    '& svg': {
      color: '#1976d2',
    },
  },

  [`& .${classes.menuItem__icon}`]: {
    color: '#546E7A',
    marginRight: 10,
  },

  [`& .${classes.backToMeetups}`]: {
    display: 'flex',
    alignItems: 'center',
    padding: '21px 16px',
  },

  [`& .${classes.backToMeetups__label}`]: {
    color: '#607D8B',
    fontSize: 11,
    whiteSpace: 'nowrap',
  },

  [`& .${classes.backToMeetups__arrow}`]: {
    color: '#546E7A',
    marginRight: 10,
    pointerEvents: 'none',
    transition: 'transform 0.2s',
  },

  [`& .${classes.backToMeetups__arrow_rotated}`]: {
    transform: 'rotate(180deg)',
  },

  [`& .${classes.divider}`]: {
    margin: '0 16px',
  },

  [`& .${classes.spacer}`]: {
    flexGrow: 1,
  },

  [`& .${classes.menuReducer}`]: {
    display: 'flex',
    padding: '26px 16px',
  },

  [`& .${classes.hider}`]: {
    display: 'none',
  },

  [`& .${classes.centerer}`]: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

const Main = props => {
  const { children } = props;

  const { mid: meetupId } = useParams();
  const location = useLocation();
  const isWideRoute = /\/m\/\d+\/visitor/.test(location.pathname);
  const appContext = useContext(AppContext);
  const [menuReduced, setMenuReduced] = useState(localStorage.getItem('menuReduced') == 'true' ? true : false);
  const [openMenu, setOpenMenu] = useState({});

  useEffect(() => {
    const storedMenuState = localStorage.getItem('openMenuState');
    if (storedMenuState) {
      setOpenMenu(JSON.parse(storedMenuState));
    } else {
      const initialOpenState = menuItems.reduce((acc, item) => {
        if (item.innerItems) acc[item.title] = true;
        return acc;
      }, {});
      setOpenMenu(initialOpenState);
    }
  }, []);

  const toggleSubMenu = title => {
    setOpenMenu(prev => {
      const newState = {
        ...prev,
        [title]: !prev[title],
      };
      localStorage.setItem('openMenuState', JSON.stringify(newState));
      return newState;
    });
  };

  const menuItems = [
    {
      title: 'Посетители',
      icon: <PeopleAltIcon className={classes.menuItem__icon} style={{ width: '20px', height: '20px' }} />,
      link: `/m/${meetupId}/visitor`,
      select: ['/group', '/settings', '/visitor'],
    },
    {
      title: 'Регистрация',
      icon: <CheckBoxOutlinedIcon className={classes.menuItem__icon} style={{ width: '20px', height: '20px' }} />,
      link: `/m/${meetupId}/online-registration`,
      innerItems: [
        {
          title: 'Онлайн',
          link: `/m/${meetupId}/online-registration`,
          select: ['/online-registration'],
        },
        {
          title: 'Офлайн',
          link: `/m/${meetupId}/desk`,
          select: ['/desk'],
        },
        {
          title: 'Редактор бейджей',
          link: `/m/${meetupId}/badge`,
          select: ['/badge'],
        },
        {
          title: 'Статистика',
          link: `/m/${meetupId}/stat/common`,
          select: ['/stat/common', '/stat/of-desks'],
        },
      ],
    },
    {
      title: 'Чат-бот Telegram',
      icon: (
        <ChatBubbleOutlineOutlinedIcon className={classes.menuItem__icon} style={{ width: '20px', height: '20px' }} />
      ),
      link: `/m/${meetupId}/chat/channels`,
      innerItems: [
        {
          title: 'Настройки',
          link: `/m/${meetupId}/chat/channels`,
          select: ['/chat/channels'],
        },
        {
          title: 'О мероприятии',
          link: `/m/${meetupId}/info`,
          select: ['/info'],
        },
        {
          title: 'Программа',
          link: `/m/${meetupId}/program/hall`,
          select: ['/program'],
        },
        {
          title: 'Новости и опросы',
          link: `/m/${meetupId}/news`,
          select: ['/news'],
        },
        {
          title: 'Нетворкинг',
          link: `/m/${meetupId}/chat/networking`,
          select: ['/chat/networking'],
        },
        {
          title: 'Переписки',
          link: `/m/${meetupId}/chat/logs`,
          select: ['/chat/logs'],
        },
      ],
    },
    {
      title: 'Рассылки',
      icon: <MailOutlineIcon className={classes.menuItem__icon} style={{ width: '20px', height: '20px' }} />,
      link: `/m/${meetupId}/mailing`,
    },
  ];

  const regexp = /\/m\/[\d-]+(\/[\w-]+).*/;
  const pathMatch = regexp.exec(window.location.pathname);
  const path = pathMatch ? pathMatch[1] : '';
  const fullPathInfo = pathMatch ? pathMatch[0] : '';
  const checkSelected = item => {
    if (item.select && item.select[0].split('/').length === 3) {
      const cc = fullPathInfo.split('/');
      const fullPath = '/' + cc.at(-2) + '/' + cc.at(-1);
      return item.select && item.select.indexOf(fullPath) !== -1;
    }
    return item.select && item.select.indexOf(path) !== -1;
  };

  return (
    <Root
      className={clsx({
        [classes.root]: true,
      })}
    >
      <Topbar />
      <div className={classes.content}>
        <Route path='/m/:mid'>
          <Drawer
            anchor='left'
            variant='permanent'
            className={clsx({
              [classes.drawer]: true,
              [classes.drawer_reduced]: menuReduced,
            })}
            classes={{
              paper: classes.drawerPaper,
            }}
            tabIndex={-1}
          >
            <Button
              className={clsx({
                [classes.backToMeetups]: true,
              })}
              underline='none'
              href={window.location.origin + '/app/meetup'}
            >
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <KeyboardBackspaceIcon
                  className={classes.backToMeetups__arrow}
                  style={{ width: '20px', height: '20px' }}
                />
                <Typography
                  className={clsx({
                    [classes.backToMeetups__label]: true,
                    [classes.hider]: menuReduced,
                  })}
                >
                  СПИСОК МЕРОПРИЯТИЙ
                </Typography>
              </div>
            </Button>
            <Divider className={classes.divider} />
            <MenuList>
              {menuItems.map(item => (
                <div key={item.title}>
                  <MenuItem component={Link} to={item.link} selected={checkSelected(item)}>
                    {item.icon}
                    <Typography>{item.title}</Typography>
                    {item.innerItems && (
                      <div
                        style={{
                          opacity: 0.5,
                          marginLeft: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        onClick={e => {
                          e.preventDefault();
                          toggleSubMenu(item.title);
                        }}
                      >
                        {openMenu[item.title] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </div>
                    )}
                  </MenuItem>
                  {item.innerItems && openMenu[item.title] && (
                    <MenuList style={{ padding: 0 }}>
                      {item.innerItems.map((innerItem, index) => (
                        <MenuItem key={index} component={Link} to={innerItem.link} selected={checkSelected(innerItem)}>
                          <Typography style={{ paddingLeft: '30px' }}>{innerItem.title}</Typography>
                        </MenuItem>
                      ))}
                    </MenuList>
                  )}
                </div>
              ))}
            </MenuList>
            <Divider className={classes.divider} />
            <MenuItem
              key={'docs'}
              component={'a'}
              selected={false}
              className={classes.menuItem}
              tabIndex={menuItems.length + 1}
              href={'/docs/'}
              target={'_blank'}
            >
              <HelpOutlineIcon className={classes.menuItem__icon} />
              <Typography className={clsx({ [classes.hider]: menuReduced })}>Документация</Typography>
            </MenuItem>

            <div className={classes.spacer}></div>

            <Button
              style={{
                background: 'inherit',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                padding: 16,
                justifyContent: 'start',
              }}
              onClick={() => {
                localStorage.setItem('menuReduced', !menuReduced);
                setMenuReduced(!menuReduced);
              }}
            >
              <KeyboardBackspaceIcon
                className={clsx({
                  [classes.backToMeetups__arrow]: true,
                  [classes.backToMeetups__arrow_rotated]: menuReduced,
                })}
              />
              <Typography
                className={clsx({
                  [classes.backToMeetups__label]: true,
                  [classes.hider]: menuReduced,
                })}
              >
                СВЕРНУТЬ МЕНЮ
              </Typography>
            </Button>
          </Drawer>
        </Route>
        <main className={classes.main}>
          <div className={clsx(classes.main__container, isWideRoute && `${classes.main__container}--wide`)}>{children}</div>
        </main>
      </div>
    </Root>
  );
};

Main.propTypes = {
  children: PropTypes.node,
};

export default Main;
